// apps/web/src/features/clients/useClientsListState.ts
//
// URL-canonical `/clients` directory state: search text, archive scope and the
// admin-only manager filter. The URL is the single source of truth so reload,
// in-app Back and browser Back all land on the same result set (AC-3).
//
// WHY the raw/debounced split: `q` must echo every keystroke into the field and
// the URL, but the wire filter may only change once typing settles — otherwise
// each character spawns a cache slot and a request. The debounce lives here so
// the page cannot forget it.
//
// INVARIANT: the wire `q` is clamped to `CLIENTS_SEARCH_MAX_CHARS`, which is the
// shared `GetClientsQuerySchema` bound. A longer paste must be visibly refused
// at the field, never turned into a 400 the list would render as "no results".
// The same holds for a hand-edited URL: an over-long `q` or a `manager` that is
// not a UUID is normalised out of the canonical query string and reported,
// because the alternative is a 400 the directory would show as an error page.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { ClientArchiveFilterSchema, type ClientArchiveFilter } from '@magimp/shared';
import type { ClientsListFilter } from '@magimp/shared-hooks';

/** Mirrors `GetClientsQuerySchema.q.max(200)`. */
export const CLIENTS_SEARCH_MAX_CHARS = 200;

/** Founder-clarified debounce window for the always-visible search field. */
export const CLIENTS_SEARCH_DEBOUNCE_MS = 275;

const PARAM_Q = 'q';
const PARAM_ARCHIVED = 'archived';
const PARAM_MANAGER = 'manager';

export interface ClientsListFilters {
  q: string;
  archived: ClientArchiveFilter;
  managerId: string;
}

export interface UseClientsListStateResult extends ClientsListFilters {
  setQ: (next: string) => void;
  setArchived: (next: ClientArchiveFilter) => void;
  setManagerId: (next: string) => void;
  resetSecondary: () => void;
  /** True while the last input was cut down to the shared maximum length. */
  qTruncated: boolean;
  /** True when a `manager` query param was unusable and had to be dropped. */
  managerInvalid: boolean;
  /** Dismisses the manager notice once the operator has seen it. */
  dismissManagerInvalid: () => void;
  /**
   * True while the field holds a query the list has NOT run yet (the debounce
   * window). The rows on screen answer the previous query, so the page must say
   * so instead of presenting them as the result of what is typed.
   */
  qPending: boolean;
  /** Number of non-default secondary (disclosed) filters currently applied. */
  secondaryActiveCount: number;
  /** Wire filter for `useClientsInfinite`; `q` is debounced and trimmed. */
  wireFilter: Omit<ClientsListFilter, 'cursor'>;
  /** Stable signature of the wire filter, used to scope the return anchor. */
  filterKey: string;
}

/** Mirrors `GetClientsQuerySchema.managerId`, which the backend rejects with a 400. */
const ManagerIdSchema = z.string().uuid();

interface ReadFiltersResult {
  filters: ClientsListFilters;
  /** The URL carried a `q` longer than the shared bound. */
  qOverflow: boolean;
  /** The URL carried a `manager` the backend would refuse. */
  managerInvalid: boolean;
}

function readFilters(params: URLSearchParams, canFilterByManager: boolean): ReadFiltersResult {
  const archived = ClientArchiveFilterSchema.safeParse(params.get(PARAM_ARCHIVED));
  const rawQ = params.get(PARAM_Q) ?? '';
  const rawManager = params.get(PARAM_MANAGER) ?? '';
  // WHY: the manager filter is admin-only server-side. Dropping it for a
  // non-admin actor keeps a shared/bookmarked URL from implying a scope the
  // backend will silently ignore.
  const managerUsable = canFilterByManager && rawManager !== '';
  const managerValid = managerUsable && ManagerIdSchema.safeParse(rawManager).success;
  return {
    filters: {
      q: rawQ.slice(0, CLIENTS_SEARCH_MAX_CHARS),
      archived: archived.success ? archived.data : 'active',
      managerId: managerValid ? rawManager : '',
    },
    qOverflow: rawQ.length > CLIENTS_SEARCH_MAX_CHARS,
    managerInvalid: managerUsable && !managerValid,
  };
}

function writeFilters(previous: URLSearchParams, next: ClientsListFilters): URLSearchParams {
  const params = new URLSearchParams(previous);
  if (next.q) params.set(PARAM_Q, next.q);
  else params.delete(PARAM_Q);
  // 'active' is the default scope; keeping it out of the URL makes the plain
  // `/clients` link and the explicitly-active URL the same result set.
  if (next.archived !== 'active') params.set(PARAM_ARCHIVED, next.archived);
  else params.delete(PARAM_ARCHIVED);
  if (next.managerId) params.set(PARAM_MANAGER, next.managerId);
  else params.delete(PARAM_MANAGER);
  return params;
}

/**
 * Rebuilds a `/clients` query string from an untrusted one, keeping ONLY the
 * three filters this page owns and only when they are usable. Used for the
 * detail page's Back target so returning lands in the same result set.
 *
 * @aiSecurity the output is re-serialised from parsed values, never echoed: a
 *   caller cannot smuggle a path, an origin or an unknown parameter through it.
 */
export function sanitizeClientsListSearch(search: string): string {
  const { filters } = readFilters(new URLSearchParams(search), true);
  const params = writeFilters(new URLSearchParams(), filters);
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * @aiPurpose owns the URL-backed `/clients` filter state and derives the
 *   debounced wire filter the list query runs on.
 * @aiInvariant every filter mutation rewrites the canonical query string with
 *   `replace: true`, so typing never floods the history stack while the last
 *   list URL still restores on Back.
 */
export function useClientsListState(canFilterByManager: boolean): UseClientsListStateResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const parsed = useMemo(
    () => readFilters(searchParams, canFilterByManager),
    [searchParams, canFilterByManager],
  );
  const filters = parsed.filters;
  const [qTruncated, setQTruncated] = useState(false);
  const [managerInvalid, setManagerInvalid] = useState(false);

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const commit = useCallback(
    (next: ClientsListFilters) => {
      setSearchParams(writeFilters(searchParamsRef.current, next), { replace: true });
    },
    [setSearchParams],
  );

  // A hand-edited or truncated-in-transit URL is normalised ONCE, in place: the
  // address bar, the field and the wire filter must not disagree, and the
  // notices below stay up after the offending param is gone.
  useEffect(() => {
    if (!parsed.qOverflow && !parsed.managerInvalid) return;
    if (parsed.qOverflow) setQTruncated(true);
    if (parsed.managerInvalid) setManagerInvalid(true);
    commit(filters);
  }, [parsed.qOverflow, parsed.managerInvalid, filters, commit]);

  const setQ = useCallback(
    (next: string) => {
      const clamped = next.slice(0, CLIENTS_SEARCH_MAX_CHARS);
      setQTruncated(clamped.length < next.length);
      if (clamped === filters.q) return;
      commit({ ...filters, q: clamped });
    },
    [commit, filters],
  );

  const setArchived = useCallback(
    (next: ClientArchiveFilter) => {
      if (next === filters.archived) return;
      commit({ ...filters, archived: next });
    },
    [commit, filters],
  );

  const setManagerId = useCallback(
    (next: string) => {
      setManagerInvalid(false);
      if (next === filters.managerId) return;
      commit({ ...filters, managerId: next });
    },
    [commit, filters],
  );

  const resetSecondary = useCallback(() => {
    setManagerInvalid(false);
    commit({ ...filters, archived: 'active', managerId: '' });
  }, [commit, filters]);

  // Debounced mirror of `q`. Seeded from the URL so a reload or a deep link
  // queries immediately instead of waiting out a phantom keystroke.
  const [debouncedQ, setDebouncedQ] = useState(filters.q.trim());
  useEffect(() => {
    const trimmed = filters.q.trim();
    if (trimmed === debouncedQ) return;
    const handle = setTimeout(() => setDebouncedQ(trimmed), CLIENTS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [filters.q, debouncedQ]);

  const wireFilter = useMemo<Omit<ClientsListFilter, 'cursor'>>(() => {
    const out: Omit<ClientsListFilter, 'cursor'> = { archived: filters.archived };
    if (debouncedQ) out.q = debouncedQ;
    if (filters.managerId) out.managerId = filters.managerId;
    return out;
  }, [filters.archived, filters.managerId, debouncedQ]);

  return {
    ...filters,
    setQ,
    setArchived,
    setManagerId,
    resetSecondary,
    qTruncated,
    managerInvalid,
    dismissManagerInvalid: () => setManagerInvalid(false),
    qPending: filters.q.trim() !== debouncedQ,
    secondaryActiveCount: (filters.archived === 'active' ? 0 : 1) + (filters.managerId ? 1 : 0),
    wireFilter,
    filterKey: `${wireFilter.archived}|${wireFilter.q ?? ''}|${wireFilter.managerId ?? ''}`,
  };
}
